<template>
	<div v-if="widget" class="widget-form display-flex flex-align-center" :class="{ 'is-mini-app': isMiniApp, 'has-logo': hasLogo }">
		<WidgetFormLogo
			v-if="hasLogo"
			class="widget-form-logo-container"
			:class="{ 'show-logo': showLogo }"
			:src="logoSrc"
			:isActive="isLogoActive"
		/>
		<v-form
			v-model="formValid"
			ref="widgetForm"
			lazy-validation
			class="widget-form-element flex-centerize"
			:class="{ 'has-top-bar': isWidgetInjection, 'wide-widget-max-width': isWideWidget, 'sticky-footer': stickyFooter }"
			:disabled="isDisableForm"
			:readonly="isReadOnly"
			@keydown.native.esc="onEscape"
			@keydown.native.enter="enterClicked"
			@submit="doneClicked"
		>
			<div class="btn-minimize-widget-container" v-if="isWidgetInjection">
				<AnemicButton small class="btn-minimize-widget" @click="$emit('minimizeWidget')" :disabled="isDisableForm && !iframeCondition">
					Minimize ↓
				</AnemicButton>
			</div>
			<div class="flex-1 display-flex flex-column height-100 max-width-100 widget-form-inner-container">
				<ScrollShadow :isShow="!isScrolledToBottom">
					<div
						ref="widgetsContainer"
						class="widgets-container"
						:class="{ 'stick-top': stickyFooter }"
						@scroll="handleScroll"
						@mouseup="handleScroll"
					>
						<div v-if="titleEnabled && titleValue" class="widget-title text-left">{{ titleValue }}</div>
						<perfect-scrollbar v-if="widgetLoading">
							<WidgetSkeletonFieldWrapper v-for="field in widget.fields" :key="field.id" :field="field" />
						</perfect-scrollbar>
						<div ref="widget_fields_container" v-else>
							<WidgetFieldWrapper
								ref="widget_field_wrapper"
								v-for="(field, i) in widgetFields"
								:field="field"
								:key="field.id"
								:isFirstElement="allowFocusOnFirstElement && i === 0"
								:fieldValue="fieldValue(field.id)"
								:previousSteps="previousSteps"
								:replaceInsertDataStringDict="replaceInsertDataStringDict"
								:configurationsMappingBlocksUUIDs="configurationsMappingBlocksUUIDs"
								:testResponseNotFlattened="testResponseNotFlattened"
								:isFullHeightForm="isFullHeightForm"
								:widgetFormScrollHeight="widgetFormScrollHeight"
								:isReadOnly="isReadOnly"
								isRuntime
								@update-value="updateValue"
								@field-event="onFieldEvent"
								@update-value-label="updateValueLabel"
								@on-mini-app-finished="doneClicked"
								@on-mini-app-cancel="cancelClicked"
								@widgetFieldUpdated="onWidgetFieldUpdated()"
								@onCloseWidget="onCloseWidget"
								@restartRun="restartRun"
								@applyInterfaceSubmit="doneClicked"
								@disable-cancel-button="disableCancelButton = true"
							/>
						</div>
					</div>
				</ScrollShadow>
				<FooterField
					v-if="!hideFooter"
					class="footer-field mt-2"
					:field="footerField"
					:previousSteps="previousSteps"
					:isRun="true"
					:isWidgetInjection="isWidgetInjection"
					:isEndRun="isEndRun"
					:isSubmitButtonDisabled="isSubmitButtonDisabled"
					:widgetLoading="widgetLoading"
					:isSubmitting="isSubmitting"
					:doneBtnLabel="doneBtnLabel"
					:plainMode="stickyFooter"
					@onCloseWidget="onCloseWidget"
					@restartRun="restartRun"
					@onDone="doneClicked"
					@onCancel="cancelClicked"
					@copyToClipboard="copyToClipboard"
					@openUrl="openUrl"
					:disableCancelButton="disableCancelButton"
				/>
			</div>
		</v-form>
		<PdfViewerHandler class="pdf-viewer" :isMultiFileViewer="isMultiFileViewer" :src="pdfSrc" v-if="showDocumentViewer" />
	</div>
</template>

<script>
import debounce from 'lodash/debounce';

import ScrollShadow from './ScrollShadow';
import WidgetFieldWrapper from './WidgetFieldWrapper';
import WidgetFormService from './WidgetFormService';
import WidgetSkeletonFieldWrapper from './WidgetSkeletonFieldWrapper';
import { CONFIRMATION_REDIRECT_OPTIONS, MINIAPPS_USING_CONFIRMATION_REDIRECT } from '../BlockEditorWidget/confirmation-mini-app.const';
import WidgetFieldService from '@/components/BlockEditorWidget/WidgetFieldService';
import FooterField from '@/components/BlockEditorWidget/WidgetFields/FooterField/FooterField';
import WidgetFormLogo from '@/components/BlockEditorWidget/WidgetFormLogo';
import widgetFieldTypes from '@/components/BlockEditorWidget/widgetFieldTypes';
import { READ_ONLY_WIDGET_FIELDS, FieldType } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import PdfViewerHandler from '@/components/WidgetForm/PdfViewer/PdfViewerHandler.vue';
import WidgetService from '@/utils/WidgetService';
import { openUrl } from '@/utils/helpers.js';
import { MULTI_FILE_VIEWER } from '@/utils/widget-service.const';

const initialState = {
	/** received from setWidget */
	api: null,
	widget: null,
	callbacks: null,
	/** init every widget render */
	fieldsValues: {},
	isSubmitting: false,
	didUpdateValueOnce: false,
	pdfSrc: '',
	formValid: false,
	iframeRedirected: false,
	isUploadInProgress: false,
	isScrollable: true,
	isFullHeightForm: false,
	disableCancelButton: false,
};

export default {
	name: 'WidgetForm',
	components: {
		PdfViewerHandler,
		WidgetFormLogo,
		WidgetFieldWrapper,
		WidgetSkeletonFieldWrapper,
		ScrollShadow,
		FooterField,
	},
	props: {
		testResponse: { type: Object },
		testResponseNotFlattened: { type: Object },
		allowFocusOnFirstElement: { type: Boolean },
		isWidgetInjection: { type: Boolean, default: false }, // needs a better name - means usage of the widget with an integration in contrast to "preview"
		isReadOnly: { type: Boolean, default: false },
		isEndRun: { type: Boolean, default: false },
		disableForm: { type: Boolean, default: false },
		stickyFooter: { type: Boolean, default: false },
	},
	computed: {
		showDocumentViewer() {
			return this.isFileViewerActive && this.pdfSrc && this.allowFocusOnFirstElement;
		},
		widgetFields() {
			return WidgetFieldService.getFormFields(this.widget?.fields);
		},
		logoSrc() {
			return this.$attrs.logoSrc;
		},
		isLogoActive() {
			return this.$attrs.isLogoActive;
		},
		hasIframeField() {
			return Boolean(this.widgetFields?.some(({ field_type }) => field_type === FieldType.Iframe));
		},
		iframeCondition() {
			return this.isIframeSubmitDisabled && this.hasIframeField && !this.iframeRedirected;
		},
		isDisableForm() {
			return this.widgetLoading || this.isSubmitting || this.iframeCondition || this.disableForm;
		},
		widgetOptions() {
			return this.widget.metadata?.options || {};
		},
		widgetLoading() {
			return this.widgetOptions.loading || false;
		},
		titleEnabled() {
			if (!('title' in this.widgetOptions)) {
				return false;
			}
			return this.widgetOptions.title.active;
		},
		multiFileViewerConfig() {
			return this.widgetOptions[MULTI_FILE_VIEWER] || { enabled: false, value: '' };
		},
		isMultiFileViewer() {
			if (this.isFileViewerActive) {
				return this.multiFileViewerConfig.enabled;
			} else {
				return false;
			}
		},
		titleValue: {
			get() {
				if (this.widgetLoading && this.widgetOptions.isBranch) {
					return 'Loading...';
				}
				return this.widgetOptions.title?.value || '';
			},
		},
		isFileViewerActive() {
			return WidgetFormService.isFileViewerActive(this.widgetOptions);
		},
		isIframeSubmitDisabled() {
			return WidgetFormService.isIframeSubmitDisabled(this.widgetOptions);
		},
		doneBtnLabel() {
			// depricated - left for backwards compatibility
			return WidgetService.getDoneBtnLabel({ widgetFields: this.widgetFields, submitText: this.widgetOptions.submit_text });
		},
		isWideWidget() {
			return this.widget.metadata?.options?.is_wide?.active;
		},
		previousSteps() {
			return this.widget.previousSteps;
		},
		replaceInsertDataStringDict() {
			return this.widget.replaceInsertDataStringDict || {};
		},
		configurationsMappingBlocksUUIDs() {
			return this.widget.configurationsMappingBlocksUUIDs || [];
		},
		isMiniApp() {
			// TODO: Hack - this should come from the server as ann attribute of 'this.widget'
			return this.widgetFields.length && ['csv-importer'].includes(this.widgetFields[0].field_type);
		},
		isSubmitButtonDisabled() {
			return [this.isDisableForm, this.isUploadInProgress, !this.allRequiredFieldsAreValid, !this.formValid].some(Boolean);
		},
		requiredFields() {
			return this.widgetFields?.filter((field) => field.metadata.options.required?.enabled);
		},
		allRequiredFieldsAreValid() {
			return this.requiredFields.every((field) => this.isFieldValid(field));
		},
		hasLogo() {
			return !this.isWidgetInjection && this.isLogoActive;
		},
		footerField() {
			return WidgetFieldService.getWidgetFooterField(this.widget?.fields || []);
		},
		hideFooter() {
			return this.isMiniAppHidingFooter();
		},
	},
	methods: {
		isMiniAppHidingFooter() {
			return (
				this.widgetFields.length &&
				MINIAPPS_USING_CONFIRMATION_REDIRECT.includes(this.widgetFields[0].field_type) &&
				this.widgetFields[0].metadata?.options?.confirmation_handling_option?.value !== CONFIRMATION_REDIRECT_OPTIONS.defaultPage
			);
		},
		fieldValue(fieldId) {
			return this.fieldsValues[fieldId];
		},
		fieldConfiguration(fieldType) {
			return widgetFieldTypes[fieldType];
		},
		isFieldValid(field) {
			const fieldValidationFunc = this.fieldConfiguration(field.field_type).validator;
			if (!fieldValidationFunc) {
				return true;
			}
			return fieldValidationFunc(this.fieldValue(field.id), field);
		},
		updateValue(payload) {
			if (!this.isReadOnly) {
				this.didUpdateValueOnce = true;
				this.updateFieldValue(payload);
			}
		},
		updateValueLabel(payload) {
			if (!this.isReadOnly && this.callbacks) {
				const { labelUpdate } = this.callbacks;
				// relevant only when using widgetForm in BlockEditor
				if (labelUpdate) {
					labelUpdate(payload);
				}
			}
		},
		onFieldEvent(payload) {
			if (!this.isReadOnly && payload.action) {
				if (payload.action === 'isUploadingFile') {
					this.isUploadInProgress = payload.value;
				} else if (payload.action === 'iframeRedirected') {
					this.iframeRedirected = true;
				} else if (payload.action === 'onMounted') {
					this.handleScroll();
					const height = this.$refs['widget_fields_container'].clientHeight;
					if (height > window.innerHeight) {
						this.$set(this, 'isFullHeightForm', true);
					}
					this.debounceOnUpdated();
				} else if (payload.action === 'setScrollable') {
					this.$set(this, 'isScrollable', payload.value);
				} else if (payload.action === 'minimizeWidget') {
					this.$emit('minimizeWidget');
				} else if (payload.action === 'maximizeWidget') {
					this.$emit('maximizeWidget');
				}
			}
		},
		setWidget(payload) {
			this.payload = payload;
			this.clearWidget(); // first lets clear the current state of the widget
			this.$nextTick(() => {
				const { widget, callbacks, api } = payload;
				let populatedWidgetFields = (widget?.fields || []).map((field) => WidgetFieldService.populateDefaultWidgetValuesIfNeeded(field)); // Make sure widgetFields Contains all relevant fields
				if (widget?.nodeRunIdPromise) {
					populatedWidgetFields = populatedWidgetFields.map((field) => ({ ...field, nodeRunIdPromise: widget?.nodeRunIdPromise }));
				}
				const populatedWidget = { ...widget, fields: populatedWidgetFields };
				this.$set(this, 'widget', populatedWidget);
				this.$set(this, 'callbacks', callbacks);
				this.$set(this, 'api', api);
				this.setInitialsFieldValues();
			});
		},
		clearWidget() {
			Object.keys(initialState).forEach((key) => {
				this.$set(this, key, initialState[key]);
			});
		},
		enterClicked(e) {
			if ((e.ctrlKey || e.metaKey) && !this.isSubmitButtonDisabled) {
				this.doneClicked();
			} else {
				e.preventDefault();
			}
		},
		async cancelClicked() {
			if (this.callbacks && this.callbacks.cancelClicked) {
				if (this.didUpdateValueOnce) {
					const res = confirm("Are you sure you want to cancel this run?\nData you've entered in this interface will be lost.");
					if (res) {
						this.callbacks.cancelClicked();
					}
				} else {
					this.callbacks.cancelClicked();
				}
			}
		},
		onEscape() {
			if (this.isWidgetInjection) {
				this.$emit('minimizeWidget');
			} else {
				this.cancelClicked();
			}
		},
		copyToClipboard(copyToClipboardText) {
			if (this.isWidgetInjection) {
				this.callbacks.copyToClipboard(copyToClipboardText);
			} else {
				navigator.clipboard
					.writeText(copyToClipboardText)
					.then(() => this.$toast.success('Copied text to clipboard!'))
					.catch(() => this.$toast.error('Something went wrong...'));
			}
		},
		openUrl({ url, isOpenInNewTab }) {
			if (this.isWidgetInjection) {
				this.callbacks.openUrl({ url, isOpenInNewTab });
			} else {
				openUrl(url, isOpenInNewTab);
			}
		},
		async doneClicked() {
			if (WidgetService.isImageEditorMiniApp(this.widget.fields[0]?.field_type)) {
				await this.$refs.widget_field_wrapper[0].invokeFieldsMethod('handleEditorProcess');
			}
			if (this.callbacks) {
				const { doneClicked } = this.callbacks;
				this.$refs.widgetForm.validate();

				if (this.formValid && doneClicked) {
					this.isSubmitting = true;
					doneClicked();
					this.clearWidget();
				}
			}
		},
		setInitialsFieldValues() {
			const { previousSteps, fields, replaceInsertDataStringDict, configurationsMappingBlocksUUIDs } = this.widget;
			fields
				.filter((field) => !READ_ONLY_WIDGET_FIELDS.includes(field.field_type))
				.forEach((widgetField) => {
					const testResponseKeys = Object.keys(this.testResponse || {});
					const isFieldInTestResponse = testResponseKeys.includes('values.' + widgetField.id) || testResponseKeys.includes(widgetField.id);
					const widgetFieldKey = 'values.' + widgetField.id;
					const value =
						this.testResponse && Object.keys(this.testResponse).length > 0 && isFieldInTestResponse
							? widgetFieldKey in this.testResponse
								? this.testResponse[widgetFieldKey]
								: this.testResponse[widgetField.id]
							: WidgetFieldService.getDefaultValueOfField(
									widgetField,
									previousSteps,
									replaceInsertDataStringDict,
									configurationsMappingBlocksUUIDs
							  );
					if (!this.isReadOnly) {
						this.updateFieldValue({ id: widgetField.id, value }, true);
					}
				});
			if (this.isFileViewerActive) {
				const fileViewerValue = this.isMultiFileViewer ? this.multiFileViewerConfig.value : this.widgetOptions.file_viewer.value;
				if (fileViewerValue.includes('{{') && fileViewerValue.includes('}}')) {
					const key = fileViewerValue.split('{{')[1].split('}}')[0];
					this.pdfSrc = this.widget.previousSteps[key] || this.translatePreviousStepsValue(fileViewerValue);
				} else {
					this.pdfSrc = fileViewerValue;
				}
			}
		},
		translatePreviousStepsValue(value) {
			return this.previousSteps
				? WidgetFormService.processPreviousSteps(
						value,
						this.previousSteps,
						this.replaceInsertDataStringDict,
						this.configurationsMappingBlocksUUIDs
				  )
				: value;
		},
		updateFieldValue(payload, fromInitialize) {
			this.setFieldValue(payload);
			if (this.callbacks) {
				this.callbacks.valueUpdate(payload, fromInitialize);
			}
		},
		setFieldValue(payload) {
			const { id, value } = payload;
			this.$set(this.fieldsValues, id, value);
		},
		minimizeWidget(payload) {
			this.sendMessageToParent({ action: 'minimizeWidget', value: payload });
		},
		handleScroll() {
			const el = (this.$refs || {}).widgetsContainer;
			this.isScrolledToBottom = el && el.clientHeight > 0 ? 2 >= el.scrollHeight - el.scrollTop - el.clientHeight : true;
			this.$set(this, 'widgetFormScrollHeight', (el || {}).scrollHeight);
			this.showLogo = this.$refs.widgetsContainer?.scrollTop < 5;
		},
		onUpdated() {
			this.$nextTick(function () {
				this.$emit('onChildUpdated');
			});
		},
		onWidgetFieldUpdated() {
			this.debounceOnUpdated();
		},
		onCloseWidget() {
			this.callbacks.doneClicked();
		},
		restartRun() {
			this.callbacks.restartRun();
		},
	},
	beforeDestroy() {
		this.clearWidget();
	},
	watch: {
		widget: {
			handler() {
				setTimeout(this.handleScroll, 100);
			},
		},
	},
	updated() {
		this.debounceOnUpdated();
	},
	created() {
		this.debounceOnUpdated = debounce(this.onUpdated, 500);
	},
	data: () => ({
		...initialState,
		isScrolledToBottom: true,
		showLogo: true,
		widgetFormScrollHeight: 0,
		disableCancelButton: false,
	}),
};
</script>

<style scoped lang="scss">
$side-pad: 40px;
.widget-form {
	$side-pad-mobile: 6px;

	border-radius: 6px !important;
	color: rgba(0, 0, 0, 0.87);

	.widget-form-element {
		margin: auto;
		position: relative;
		height: fit-content;
		background-color: #ffffff;

		.skeleton-actions {
			cursor: default;
			::v-deep {
				.v-skeleton-loader__actions {
					padding: 0px;
				}
			}
		}

		.widgets-container {
			position: relative;
			min-height: 120px;
		}

		&.has-top-bar {
			margin-top: $widget-top-actions-bar-height;
		}

		.widget-title {
			font-size: 25px;
			font-family: Poppins;
			font-weight: 500;
			color: #262626;
		}

		&.wide-widget-max-width {
			width: 100%;
			max-width: 90vw !important;
		}

		&.sticky-footer {
			.footer-field {
				position: sticky;
				bottom: 0;
				z-index: 1;
				background-color: #fff;
				height: 60px;
			}
			&.expanded {
				box-shadow: 0px -3px 6px #00000029;
			}
			::v-deep {
				.default-widget-field .default-widget-field-title {
					font-size: 13px !important;
					color: #262626 !important;
					font-weight: normal !important;
				}
			}
		}
	}

	.widget-form-logo-container {
		position: absolute;
		top: 14px;
		left: 14px;
	}

	.buttons-section {
		padding: 14px 20px;
	}

	.btn-minimize-widget-container {
		position: absolute;
		top: -($widget-top-actions-bar-height);
		right: 0;

		.btn-minimize-widget {
			height: 21px !important;
			font-size: 12px !important;
		}
	}

	&.is-mini-app {
		.buttons-section {
			display: none;
		}
	}
	/* Responsive */

	/*
		Refrain from using layout adjustments here (height, width, margins, padding etc.)
		WidgetForm is being used by several components with different layouts 
		and every change here has to be tested and verified that it doesn't break changes there..
	*/
	@media (max-width: $device-sm) {
		::v-deep(.scroll-shadow-container) {
			display: none;
		}
		.widgets-container:not(.stick-top) {
			padding-top: 80px !important;
		}

		&.has-logo {
			.widget-form-logo-container {
				&.show-logo {
					opacity: 1;
					transition: 1s;
				}
				&:not(.show-logo) {
					top: 0;
					opacity: 0;
					transition: 1s;
				}
			}
		}
	}

	@media (max-width: 1400px) {
		&.has-logo .widget-form-element.wide-widget-max-width {
			max-width: 80vw !important;
		}
	}
}

.powered-by {
	font-size: 10px;
	color: var(--secondary-font-color);
}
.pdf-viewer {
	max-width: calc(100vw - 2 * #{$side-pad} - var(--widget-form-width));
}
</style>
